import { debug, warn, error as logError } from '@/utils/logger';

export type SocialUrlValidationFailureReason =
  | 'empty'
  | 'invalid_url'
  | 'unsupported_platform'
  | 'invalid_post_path';

export type SocialMediaUrlValidationResult = {
  isValid: boolean;
  error?: string;
  reason?: SocialUrlValidationFailureReason;
  normalizedUrl?: string;
};

/**
 * Normalizes pasted input for URL parsing (trim, add https when missing).
 */
export function normalizeSocialMediaUrlInput(url: string): string {
  const trimmed = url.trim();
  if (!trimmed) return trimmed;
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  if (trimmed.startsWith('//')) return `https:${trimmed}`;
  return `https://${trimmed}`;
}

/**
 * Validates if a URL is a valid Instagram or TikTok post URL
 * @param url - The URL string to validate
 * @returns Object with isValid boolean and error message if invalid
 */
export function validateSocialMediaUrl(url: string): SocialMediaUrlValidationResult {
  debug('[urlValidation] validateSocialMediaUrl called', { url });

  if (typeof url !== 'string') {
    warn('[urlValidation] Invalid input: not a string', { url, type: typeof url });
    return { isValid: false, error: 'Paste a valid Instagram or TikTok link.', reason: 'invalid_url' };
  }

  const trimmed = url.trim();
  if (!trimmed) {
    warn('[urlValidation] Empty string after trim');
    return { isValid: false, error: 'Paste a link to an Instagram or TikTok post.', reason: 'empty' };
  }

  const normalizedUrl = normalizeSocialMediaUrlInput(trimmed);

  try {
    const urlObj = new URL(normalizedUrl);

    debug('[urlValidation] URL parsed', {
      hostname: urlObj.hostname,
      pathname: urlObj.pathname,
      protocol: urlObj.protocol,
    });

    if (!urlObj.hostname.includes('.')) {
      warn('[urlValidation] Hostname is not a valid domain', { hostname: urlObj.hostname });
      return {
        isValid: false,
        error: 'Paste a valid Instagram or TikTok link.',
        reason: 'invalid_url',
      };
    }

    const isInstagram =
      urlObj.hostname.includes('instagram.com') || urlObj.hostname.includes('instagr.am');

    const isTikTok =
      urlObj.hostname.includes('tiktok.com') || urlObj.hostname.includes('vm.tiktok.com');

    debug('[urlValidation] Platform detection', { isInstagram, isTikTok });

    if (!isInstagram && !isTikTok) {
      warn('[urlValidation] Unsupported platform', { hostname: urlObj.hostname });
      return {
        isValid: false,
        error: 'Only Instagram and TikTok links are supported.',
        reason: 'unsupported_platform',
      };
    }

    if (isInstagram) {
      const hasPostPath =
        urlObj.pathname.includes('/p/') ||
        urlObj.pathname.includes('/reel/') ||
        urlObj.pathname.includes('/reels/') ||
        urlObj.pathname.includes('/tv/');
      debug('[urlValidation] Instagram path check', {
        pathname: urlObj.pathname,
        hasPostPath,
      });
      if (!hasPostPath) {
        warn('[urlValidation] Instagram URL missing post path');
        return {
          isValid: false,
          error:
            'Paste a link to an Instagram post or Reel (for example instagram.com/reel/… or instagram.com/p/…).',
          reason: 'invalid_post_path',
        };
      }
    }

    if (isTikTok) {
      const hasVideoPath = urlObj.pathname.includes('/video/');
      const isShortLink =
        urlObj.hostname.includes('vm.tiktok.com') || urlObj.pathname.includes('/t/');
      debug('[urlValidation] TikTok path check', {
        pathname: urlObj.pathname,
        hasVideoPath,
        isShortLink,
      });
      if (!hasVideoPath && !isShortLink) {
        warn('[urlValidation] TikTok URL missing video path');
        return {
          isValid: false,
          error:
            'Paste a link to a TikTok video (for example tiktok.com/@user/video/… or vm.tiktok.com/…).',
          reason: 'invalid_post_path',
        };
      }
    }

    debug('[urlValidation] Validation successful', {
      platform: isInstagram ? 'Instagram' : 'TikTok',
      url: normalizedUrl,
    });

    return { isValid: true, normalizedUrl };
  } catch (error) {
    logError('[urlValidation] URL parsing error', {
      error,
      url: normalizedUrl,
    });
    return {
      isValid: false,
      error: 'Paste a valid Instagram or TikTok link.',
      reason: 'invalid_url',
    };
  }
}
